import type { PageContent } from "../types";

export const p_how_accountancy_websites_can_improve_enquiry_quality: PageContent = {
  slug: "how-accountancy-websites-can-improve-enquiry-quality",
  title: `How Accountancy Websites Can Improve Enquiry Quality | Phil Carr SEO Consultant`,
  description: `How accountancy firms can restructure their websites and SEO to attract better-qualified clients — moving away from compliance-only enquiries to higher-value, longer-retained engagements.`,
  date: "2026-04-05",
  lastUpdated: "2026-05-24",
  body: `# How Accountancy Websites Can Improve Enquiry Quality

**Published:** 2026-04-05

## TL;DR

Most accountancy firm websites collect enquiries from the wrong end of the market — sole traders chasing a £150 self-assessment when the practice would prefer £1,500-a-month bookkeeping and advisory clients. Better SEO doesn't mean more enquiries; it means restructuring the website so the right clients self-select before they fill in the form. This article covers the practical pages, copy and signals that move accountancy enquiry quality up the value scale.

## Why accountancy SEO is mostly an enquiry-quality problem

Demand for accountants is steady — people always need one. The issue is rarely traffic. It's that most accountancy websites read like a service list any practice in the country could publish: "tax returns, bookkeeping, VAT, payroll, year-end accounts". A search engine has nothing to differentiate them on, and a prospective client has nothing to self-qualify against.

The firms that win the better-fit clients have done three things on their site:

1. Picked a clear primary audience (industry vertical, business size, or service depth).
2. Built specific pages that match the language that audience searches with.
3. Used the contact flow to filter out enquiries that aren't a fit.

The rest of this guide walks through how to do each.

## Step 1 — Define the client you actually want

Pick the segment that already pays you well and that you'd like more of. The most common high-value segments for UK practices are:

- Owner-managed limited companies turning over £250k–£3m.
- Property landlords with three or more residential lets.
- Tech and SaaS founders with R&D claim potential.
- Construction and trades operating CIS.
- Hospitality groups with multi-site VAT complexity.

Whichever you choose becomes the spine of your SEO strategy. Your homepage hero, your three top-level service pages, and your case studies should all visibly speak to that audience.

## Step 2 — Build vertical landing pages, not service lists

Generic service pages ("Bookkeeping", "VAT returns") rank against thousands of other identical pages. **Vertical pages** ("Accountants for property landlords", "R&D tax credits for SaaS startups", "Construction accountants and CIS specialists") rank against a handful — and they convert at 3–5× the rate because the visitor immediately knows the firm understands them.

Each vertical page should cover:

- The specific pain points that vertical faces (cash-flow timing, sector-specific allowances, regulatory deadlines).
- The exact services you offer to address them, priced or "from" priced.
- A case study or two with real numbers.
- Sector-specific FAQs (these earn AI Overview citations and featured snippets).
- A clearly different contact CTA — "Book a 20-minute landlord finance review", not "Get in touch".

If your current site is built around generic service descriptions, our [bespoke website audit](/seo/bespoke-seo-website-audits) will identify the rewrites with the highest expected impact.

## Step 3 — Treat E-E-A-T as a primary SEO signal

Accountancy is a [Google "Your Money or Your Life" (YMYL) category](/blog/eeat-and-content-quality), so Experience, Expertise, Authoritativeness and Trustworthiness matter more than for most industries. Practical changes:

- Named author bios on every article and key service page — full name, qualifications (ACA, ACCA, CTA, AAT), years in practice, photo.
- Practice information: registration with ICAEW / ACCA / CIOT, professional indemnity, anti-money-laundering registration number.
- Real reviews — Google Business Profile reviews carry the most ranking weight; supplement with Trustpilot or VouchedFor.
- Schema markup for AccountingService, FAQPage and Person on bios.
- No AI-written content that hasn't been edited and signed by a named partner. Google's helpful content systems are explicitly hostile to unedited generative copy in financial verticals.

## Step 4 — Local + vertical = the sweet spot

National accounting SEO ("accountants UK") is brutally competitive and dominated by the volume players. The opportunity for most independent practices is the intersection of vertical and location: "construction accountants Barnsley", "property accountants Sheffield", "SaaS accountants Leeds". These terms have manageable competition and convert exceptionally well.

Build a clean structure: vertical landing page → location variant of that vertical page → general location page (see [SEO services for Barnsley businesses](/areas/barnsley) for the area-page pattern). Don't doorway-page it; each location variant needs genuinely different copy and at least one local case study.

## Step 5 — Filter at the form, not at the call

The contact form is your last line of defence against low-value enquiries. The fields you choose dictate the leads that land in your inbox:

- **Business type** (Limited company / sole trader / partnership / LLP / individual) — most firms want to discourage individuals.
- **Approximate turnover band** — make the bottom band reflect your minimum client size.
- **Current accountant** (yes / no / looking to switch) — switchers are higher value than first-time setups.
- **Services interested in** — multi-select from your real menu.
- **Sector** — visible only if you serve multiple verticals.

An enquiry that takes 90 seconds to fill in is worth ten enquiries that take 10.

## Step 6 — Content that earns AI Overview citations

AI search now drives a meaningful share of accountancy queries — VAT thresholds, R&D claim eligibility, IR35 status, capital allowances. Firms that earn AI Overview citations get prominent attribution without losing the click, because the user often follows the citation to verify. The content pattern that works:

- Answer one clear question per page in the first 50 words.
- Cite the underlying HMRC or legislative source.
- Update the article when the rule changes and reflect that in the published / last-updated date.
- Use FAQPage schema with real questions and concise answers.

Our [AI-search 90-day playbook](/blog/a-practical-plan-for-ai-driven-search-a-ninety-day-playbook) covers the broader strategy for getting picked up by AI Overviews and Perplexity.

## Common mistakes that hurt accountancy enquiry quality

- Listing every service with equal prominence — the homepage tries to be everything to everyone.
- "Free quote" CTAs that attract price shoppers. "Book a discovery call" attracts buyers.
- Stock photos of stacks of coins and people in suits shaking hands.
- Hiding partner names and qualifications below the fold or on a separate "about" page.
- A blog that hasn't been updated since 2023 — for YMYL sites, stale content is actively damaging.

## Want help raising enquiry quality?

If your firm is getting enquiries but the mix is wrong, the fix is almost always structural rather than promotional. Start with the service framework on [SEO for accountants](/seo/sectors/accountants), then [book a free consultation](/contact) and we'll walk through your current site, your top-three target client types, and the three highest-leverage changes for the next 90 days.

## Update — May 2026

Reviewed in May 2026. Nothing structural has changed in how I'd approach this — the shift is that clients are asking for shorter, sharper reporting cycles, and the framework below adapts to that without needing a rewrite.
`,
};
